// PDF first-page thumbnail renderer. Used by AttachmentCard so a forwarded
// email's PDF attachment can show a recognisable preview rather than just
// a 📄 emoji.
//
// pdfjs is lazy-loaded — the bundle is ~1MB and only a fraction of users
// will ever expand a record that has a PDF, so paying the cost up front
// (every cold load) would be wasteful.

import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

type PdfjsModule = typeof import("pdfjs-dist");

let _pdfjs: PdfjsModule | null = null;

async function getPdfjs(): Promise<PdfjsModule> {
  if (_pdfjs) return _pdfjs;
  const mod = await import("pdfjs-dist");
  mod.GlobalWorkerOptions.workerSrc = workerSrc;
  _pdfjs = mod;
  return mod;
}

// Render the first page of a PDF to a PNG data URL at the given target
// width. Returns null if rendering fails (corrupt PDF, encrypted PDF,
// pdfjs load failure, etc.) so the caller can fall back to the icon.
export async function renderPdfThumbnail(
  bytes: ArrayBuffer,
  targetWidth: number = 160,
): Promise<string | null> {
  try {
    const pdfjs = await getPdfjs();
    const doc = await pdfjs.getDocument({
      data: bytes,
      // Suppress pdfjs's noisy console warnings about missing font data —
      // we're only rendering a thumbnail and don't care about glyph
      // fidelity.
      verbosity: 0,
    }).promise;
    const page = await doc.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = targetWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

export interface RenderedPdfPage {
  dataUrl: string; // JPEG data URL
  width: number; // rendered px
  height: number; // rendered px
}

export interface RenderedPdf {
  pages: RenderedPdfPage[];
  totalPages: number; // total in the document (may exceed pages.length)
}

// Render up to `maxPages` of a PDF to JPEG data URLs for embedding in
// another PInline document (the dossier export). JPEG (not PNG) keeps
// the embedded bytes manageable for large multi-page scans. Each page
// is rendered, captured, then its canvas is released before the next so
// memory stays bounded. Individual page failures are skipped, not
// fatal; a total failure (corrupt/encrypted PDF) returns null.
export async function renderPdfPages(
  bytes: ArrayBuffer,
  opts: { maxPages?: number; targetWidth?: number; quality?: number } = {},
): Promise<RenderedPdf | null> {
  const maxPages = opts.maxPages ?? 30;
  const targetWidth = opts.targetWidth ?? 1000;
  const quality = opts.quality ?? 0.72;
  try {
    const pdfjs = await getPdfjs();
    // Copy into a fresh Uint8Array — pdfjs may detach the buffer.
    const doc = await pdfjs.getDocument({
      data: new Uint8Array(bytes),
      verbosity: 0,
    }).promise;
    const totalPages = doc.numPages;
    const limit = Math.min(totalPages, maxPages);
    const pages: RenderedPdfPage[] = [];
    for (let i = 1; i <= limit; i++) {
      try {
        const page = await doc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = targetWidth / base.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport }).promise;
        pages.push({
          dataUrl: canvas.toDataURL("image/jpeg", quality),
          width: viewport.width,
          height: viewport.height,
        });
        // Release the canvas backing store before the next page.
        canvas.width = 0;
        canvas.height = 0;
      } catch {
        /* skip this page, keep going */
      }
    }
    return { pages, totalPages };
  } catch {
    return null;
  }
}
